import { Rally, SetRecord, TeamState, CategoryAssignment, VolleyballCategory } from '@/types';
import { useMemo } from 'react';

interface RallyHistoryModalProps {
  setNumber: number;
  setRecord: SetRecord;
  rallies: Rally[];
  teamA: TeamState;
  teamB: TeamState;
  onClose: () => void;
}

export default function RallyHistoryModal({
  setNumber,
  setRecord,
  rallies,
  teamA,
  teamB,
  onClose,
}: RallyHistoryModalProps) {
  const allPlayers = [...teamA.players, ...teamB.players];
  const isCompleted = setRecord.endedAt !== null;
  const winner = setRecord.winnerId
    ? setRecord.winnerId === teamA.id ? teamA : teamB
    : null;

  // Rallies are expected ascending (rally 1 first)
  const sortedRallies = [...rallies].sort((a, b) => a.rallyNumber - b.rallyNumber);

  // Running score after each rally (ascending order matches sortedRallies)
  const rallyScores: { a: number; b: number }[] = [];
  { let rA = 0, rB = 0;
    for (const r of sortedRallies) {
      if (r.pointWonById === teamA.id) rA++;
      else if (r.pointWonById === teamB.id) rB++;
      rallyScores.push({ a: rA, b: rB });
    }
  }

  // Calculate score from rally count (for accuracy)
  const calculatedScore = useMemo(() => {
    let scoreA = 0;
    let scoreB = 0;
    for (const rally of sortedRallies) {
      if (rally.pointWonById === teamA.id) scoreA++;
      else if (rally.pointWonById === teamB.id) scoreB++;
    }
    return { scoreA, scoreB };
  }, [sortedRallies, teamA.id, teamB.id]);

  // Helper to format categories for display with team colors
  const formatCategoriesDisplay = (categories?: CategoryAssignment[]): React.ReactNode => {
    if (!categories || categories.length === 0) return null;

    // Group by category with team color info
    const grouped = new Map<VolleyballCategory, Array<{ name: string; color: string }>>();
    for (const c of categories) {
      const player = allPlayers.find(p => p.id === c.playerId);
      const playerTeam = teamA.players.some(p => p.id === c.playerId) ? teamA : teamB;
      const name = player?.name?.split(' ')[0] ?? '?';
      const list = grouped.get(c.category) || [];
      list.push({ name, color: playerTeam.color });
      grouped.set(c.category, list);
    }

    // Render in order: A, D, L, S, B, F
    const order: VolleyballCategory[] = ['A', 'D', 'L', 'S', 'B', 'F'];
    const elements: React.ReactNode[] = [];

    for (const cat of order) {
      const entries = grouped.get(cat);
      if (entries && entries.length > 0) {
        const namesWithColors = entries.map(e => (
          <span key={`${cat}-${e.name}`} style={{ color: e.color }}>{e.name}</span>
        ));
        // Join names with commas (as React elements)
        const joined = namesWithColors.reduce<React.ReactNode[]>((acc, curr, idx) => {
          if (idx === 0) return [curr];
          return [...acc, <span key={`comma-${idx}`} className="text-text-muted">, </span>, curr];
        }, []);
        elements.push(
          <span key={cat} className="font-medium">
            <span className="text-text-secondary">{cat}:</span>
            {joined}
          </span>
        );
      }
    }

    return elements.length > 0
      ? <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">{elements}</div>
      : null;
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="bg-surface border-b border-border px-4 py-4 safe-top flex items-center gap-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-2 -ml-2 rounded-lg text-text-muted hover:text-text-primary transition-colors"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 4l-6 6 6 6" />
          </svg>
        </button>
        <div className="flex-1">
          <h2 className="text-base font-bold text-text-primary">
            SET {setNumber} — RALLY HISTORY
          </h2>
          <p className="text-xs text-text-muted">
            {sortedRallies.length} {sortedRallies.length === 1 ? 'rally' : 'rallies'}
            {isCompleted && winner ? ` · ${winner.name} won` : ''}
          </p>
        </div>
      </div>

      {/* Score Summary */}
      <div className="bg-surface border-b border-border px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-center gap-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase" style={{ color: teamA.color }}>
              {teamA.shortName}
            </p>
            <p className="text-3xl font-bold text-text-primary font-mono-nums">
              {String(calculatedScore.scoreA).padStart(2, '0')}
            </p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-text-muted text-xs font-bold">vs</span>
            {winner && (
              <span
                className="text-xs font-bold px-2 py-0.5 rounded"
                style={{ backgroundColor: `${winner.color}30`, color: winner.color }}
              >
                {winner.shortName} WON
              </span>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase" style={{ color: teamB.color }}>
              {teamB.shortName}
            </p>
            <p className="text-3xl font-bold text-text-primary font-mono-nums">
              {String(calculatedScore.scoreB).padStart(2, '0')}
            </p>
          </div>
        </div>
      </div>

      {/* Rally List */}
      <div className="flex-1 overflow-y-auto">
        {sortedRallies.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-text-muted text-sm">No rallies recorded.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedRallies.map((rally, idx) => {
              const rallyServingTeam = rally.servingTeamId === teamA.id ? teamA : teamB;
              const rallyWinningTeam = rally.pointWonById === teamA.id ? teamA : teamB;
              const server = allPlayers.find(p => p.id === rally.serverId);
              const isServiceBreak = rally.pointWonById !== rally.servingTeamId;
              const categoriesDisplay = formatCategoriesDisplay(rally.categories);
              const isServeFoul = rally.lostReason === 'FOUL';
              const isServerFault = rally.isServerFault === true;
              const score = rallyScores[idx];

              return (
                <div
                  key={rally.id}
                  className={`px-3 py-2.5 text-xs ${
                    isServeFoul ? 'bg-warning/10' : isServiceBreak ? 'bg-error/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {/* Rally number */}
                    <span className="w-7 text-center text-text-muted font-mono-nums">
                      {rally.rallyNumber}
                    </span>
                    {/* Serving team badge */}
                    <span
                      className="w-8 font-bold text-center"
                      style={{ color: rallyServingTeam.color }}
                    >
                      {rallyServingTeam.shortName}
                    </span>
                    {/* Server name, FOUL indicator, or SERVER_FAULT system event */}
                    <span className="flex-1 text-text-secondary truncate">
                      {isServerFault ? (
                        <span className="text-error font-semibold">SERVER_FAULT</span>
                      ) : isServeFoul ? (
                        <span className="text-warning font-semibold">SF: {server?.name ?? '—'}</span>
                      ) : (
                        server?.name ?? '—'
                      )}
                    </span>
                    {/* Arrow */}
                    <span className="w-3 text-center text-text-muted">→</span>
                    {/* Winning team badge */}
                    <span
                      className="w-8 font-bold text-center"
                      style={{ color: rallyWinningTeam.color }}
                    >
                      {rallyWinningTeam.shortName}
                    </span>
                    {/* Running score */}
                    <span className="font-mono font-bold text-[11px] ml-auto pl-2 shrink-0">
                      <span style={{ color: teamA.color }}>{score.a}</span>
                      <span className="text-text-muted">–</span>
                      <span style={{ color: teamB.color }}>{score.b}</span>
                    </span>
                  </div>
                  {/* Categories row */}
                  {categoriesDisplay && (
                    <div className="mt-1 pl-[4.25rem]">
                      {categoriesDisplay}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
